import { useEffect, useState } from 'react';

import BottomSheet from '@perpay-web/components/base/BottomSheet/BottomSheet';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useToastContext } from '@perpay-web/components/base/ToastProvider/useToastContext';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import { AddBankAccountButton } from '@perpay-web/fintech/components/composite/AddBankAccountButton/AddBankAccountButton';
import RadioBankAccountDisplay from '@perpay-web/fintech/components/composite/BankAccountDisplay/RadioBankAccountDisplay';
import { useLoadBankAccounts } from '@perpay-web/fintech/hooks/useLoadBankAccounts';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './ChooseBankModal.scss';

/**
 * @param {Object} props
 * @param {Function} props.onBack
 * @param {Function} props.onSuccess
 * @param {('card')} [props.buttonTheme] - The theme of the button to use for the save changes button.
 * @param {boolean} [props.skipMFA] - Whether to skip MFA for the add bank account flow.
 * @returns {JSX.Element}
 */
export const ChooseBankModal = ({
    onBack,
    onSuccess,
    buttonTheme,
    skipMFA,
}) => {
    const { bankAccounts } = useLoadBankAccounts();
    const { addToast } = useToastContext();
    const [selectedBankAccountUuid, setSelectedBankAccountUuid] =
        useState(null);

    useEffect(() => {
        if (bankAccounts.length > 0) {
            if (!selectedBankAccountUuid) {
                setSelectedBankAccountUuid(bankAccounts[0].uuid);
            }
        }
    }, [bankAccounts, selectedBankAccountUuid]);

    const handleSubmit = () => {
        onSuccess(
            bankAccounts.find(
                (bankAccount) => bankAccount.uuid === selectedBankAccountUuid,
            ),
        );
    };

    return (
        <BottomSheet
            aria-label='Choose Bank Modal'
            onClose={onBack}
            contentClassName={styles['full-width']}
        >
            <FocusLayout className={styles['focus-layout']}>
                <FocusHeader className={styles['focus-header']} />
                <FocusContent className={styles['focus-content']}>
                    <Stack
                        className={getClassName(
                            styles['inner-stack'],
                            styles['small-gap'],
                        )}
                    >
                        <div className={styles.title}>Choose Bank</div>
                        {bankAccounts.map((bankAccount) => (
                            <RadioBankAccountDisplay
                                key={bankAccount.uuid}
                                bankAccount={bankAccount}
                                isSelected={
                                    selectedBankAccountUuid === bankAccount.uuid
                                }
                                onSelect={() =>
                                    setSelectedBankAccountUuid(bankAccount.uuid)
                                }
                            />
                        ))}
                    </Stack>
                    <Stack
                        className={getClassName(
                            styles['inner-stack'],
                            styles['larger-gap'],
                        )}
                    >
                        <AddBankAccountButton
                            onSuccess={(bank) => {
                                onSuccess(bank);
                            }}
                            onFailure={() => {
                                addToast({
                                    message:
                                        'Unable to add bank account.  Please check your bank account details and try again, or use a different bank account.',
                                    type: 'error',
                                });
                            }}
                            buttonTheme={buttonTheme}
                            skipMFA={skipMFA}
                        />
                    </Stack>
                </FocusContent>
                <FocusFooter>
                    {buttonTheme === 'card' ? (
                        <CardCta
                            Component={CustomButton}
                            mode='dark'
                            type='submit'
                            onClick={handleSubmit}
                            disabled={!selectedBankAccountUuid}
                        >
                            Save Changes
                        </CardCta>
                    ) : (
                        <CustomButton
                            action='primary'
                            disabled={!selectedBankAccountUuid}
                            onClick={handleSubmit}
                        >
                            Save Changes
                        </CustomButton>
                    )}
                </FocusFooter>
            </FocusLayout>
        </BottomSheet>
    );
};
